/**
 * CompositePass — purely-spatial transformation of the post-warp/seed
 * texture. Sits between the feedback ring and the bloom/present chain.
 *
 * The output is written to a dedicated full-internal-resolution texture so
 * the feedback loop is unaffected (mirror modes don't compound across
 * frames). When the active preset doesn't request any composite operation
 * we still run this pass with mode=None; that's a single draw call, much
 * cheaper than juggling an "is the composite enabled" code path through
 * the engine and present pass.
 */

import compositeWgsl from '../shaders/composite.wgsl?raw';

export const enum MirrorMode {
  None = 0,
  Horizontal = 1,
  Vertical = 2,
  FourWay = 3,
  Kaleidoscope = 4,
}

export type CompositeParams = {
  mode: MirrorMode;
  /** Number of kaleidoscope segments (only used when mode = Kaleidoscope). */
  segments: number;
  /** 1.0 = no contrast change. */
  contrast: number;
  /** 0.0 = no brightness change. */
  brightness: number;
};

const PARAMS_BYTES = 16; // u32 + 3 f32

export class CompositePass {
  private readonly device: GPUDevice;
  private readonly format: GPUTextureFormat = 'rgba16float';
  private readonly sampler: GPUSampler;
  private readonly paramsBuffer: GPUBuffer;
  private readonly cpu = new ArrayBuffer(PARAMS_BYTES);
  private readonly cpuU32 = new Uint32Array(this.cpu);
  private readonly cpuF32 = new Float32Array(this.cpu);

  private readonly bgl: GPUBindGroupLayout;
  private readonly pipeline: GPURenderPipeline;

  private outTex!: GPUTexture;
  private outView!: GPUTextureView;
  private width = 0;
  private height = 0;

  constructor(device: GPUDevice, width: number, height: number) {
    this.device = device;

    this.sampler = device.createSampler({
      label: 'composite-sampler',
      magFilter: 'linear',
      minFilter: 'linear',
      addressModeU: 'clamp-to-edge',
      addressModeV: 'clamp-to-edge',
    });

    this.paramsBuffer = device.createBuffer({
      label: 'composite-params',
      size: PARAMS_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const shader = device.createShaderModule({
      label: 'composite-shader',
      code: compositeWgsl,
    });

    this.bgl = device.createBindGroupLayout({
      label: 'composite-bgl',
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d' },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          sampler: { type: 'filtering' },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform' },
        },
      ],
    });

    this.pipeline = device.createRenderPipeline({
      label: 'composite-pipeline',
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.bgl] }),
      vertex: { module: shader, entryPoint: 'vs_main' },
      fragment: {
        module: shader,
        entryPoint: 'fs_main',
        targets: [{ format: this.format }],
      },
      primitive: { topology: 'triangle-list' },
    });

    this.resize(width, height);
  }

  resize(width: number, height: number): void {
    if (width === this.width && height === this.height && this.outTex) return;
    this.outTex?.destroy();
    this.width = width;
    this.height = height;
    this.outTex = this.device.createTexture({
      label: 'composite-out',
      size: { width, height },
      format: this.format,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.outView = this.outTex.createView();
  }

  getOutputView(): GPUTextureView {
    return this.outView;
  }

  encode(
    encoder: GPUCommandEncoder,
    sourceView: GPUTextureView,
    params: CompositeParams,
  ): void {
    this.cpuU32[0] = params.mode >>> 0;
    this.cpuF32[1] = params.segments;
    this.cpuF32[2] = params.contrast;
    this.cpuF32[3] = params.brightness;
    this.device.queue.writeBuffer(this.paramsBuffer, 0, this.cpu);

    const bg = this.device.createBindGroup({
      layout: this.bgl,
      entries: [
        { binding: 0, resource: sourceView },
        { binding: 1, resource: this.sampler },
        { binding: 2, resource: { buffer: this.paramsBuffer } },
      ],
    });

    const pass = encoder.beginRenderPass({
      label: 'composite-pass',
      colorAttachments: [
        {
          view: this.outView,
          loadOp: 'clear',
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          storeOp: 'store',
        },
      ],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, bg);
    pass.draw(6, 1, 0, 0);
    pass.end();
  }

  destroy(): void {
    this.outTex?.destroy();
  }
}
